import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import AvatarUpload from '../../../components/ui/AvatarUpload';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../../store/authSlice';
import Input from '../../../components/ui/Input';
import { Mail, Briefcase, Hash, Building2, User } from 'lucide-react';

const GeneralTab = () => {

  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!user?.email) return;
      
      try {
        const { data, error } = await supabase
          .from('employees')
          .select('*, departments(name)')
          .eq('email', user.email)
          .single();
          
        if (error && error.code !== 'PGRST116') {
          console.error("Error fetching employee:", error);
        } else if (data) {
          setEmployeeData(data);
        }
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmployeeData();
  }, [user]);

  const handleAvatarUpload = (newUrl) => {
    dispatch(setUser({ ...user, avatar_url: newUrl }));
  };

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground animate-pulse">Loading profile data...</div>;
  }

  const roleDisplay = user?.role === 'system_admin' ? 'System Administrator' : 
                      user?.role === 'admin' ? 'Administrator' : 
                      user?.role === 'department_pic' ? 'Department PIC' : 
                      'IT Staff';

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Profile Header Card */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        {/* Cover Photo / Background */}
        <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600 w-full" />
        
        <div className="px-8 pb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 -mt-16 sm:-mt-12 mb-6">
            <div className="relative">
              <AvatarUpload 
                userId={user?.id}
                url={user?.avatar_url}
                fullName={user?.full_name}
                onUploadComplete={handleAvatarUpload}
              />
            </div>
            
            <div className="text-center sm:text-left flex-1 pb-2">
              <h1 className="text-2xl font-bold text-foreground tracking-tight">{user?.full_name}</h1>
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/20 text-blue-800">
                  {roleDisplay}
                </span>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg border border-border/50 text-center sm:text-left">
            Your profile picture helps your team members recognize you in the system. 
            Click on your avatar to upload a new one.
          </p>
        </div>
      </div>
      {/* Linked Employee Information */}
      <div className="bg-card rounded-2xl border border-border shadow-sm">
        <div className="px-8 py-6 border-b border-border">
          <h2 className="text-lg font-bold text-foreground">Employee Information</h2>
          <p className="text-sm text-muted-foreground">Linked physical employee data from HR records</p>
        </div>
        
        <div className="p-8">
          {employeeData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-1">
                  <User size={14} />Full Name</label>
                <div className="font-medium text-foreground bg-muted/30 px-4 py-3 rounded-lg border border-border/50">
                  {employeeData.full_name || 'N/A'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-1">
                  <Mail size={14} />Email Address</label>
                <div className="font-medium text-foreground bg-muted/30 px-4 py-3 rounded-lg border border-border/50">
                  {employeeData.email || 'N/A'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-1">
                  <Hash size={14} />Employee ID</label>
                <div className="font-medium text-foreground bg-muted/30 px-4 py-3 rounded-lg border border-border/50">
                  {employeeData.employee_id || employeeData.employee_number || 'N/A'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-1">
                  <Building2 size={14} />Department</label>
                <div className="font-medium text-foreground bg-muted/30 px-4 py-3 rounded-lg border border-border/50">
                  {employeeData.departments?.name || 'N/A'}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                <Briefcase className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">No linked employee record</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                We couldn't find a matching employee record for your email. Please contact your administrator if this is a mistake.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;
